import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemText, ListItemSecondaryAction,
  Typography, Chip, Box, LinearProgress, Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Star as StarIcon, Warning as WarningIcon,
  CheckCircle as CheckIcon, Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon
} from '@material-ui/icons';
import { suggestCrewAssignments, getTopSuggestions } from './crewAssignmentSuggestions';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  suggestionItem: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  topSuggestion: {
    backgroundColor: theme.palette.success.light,
    borderColor: theme.palette.success.main,
  },
  scoreContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  confidenceChip: {
    minWidth: 60,
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  crewColorBar: {
    width: 4,
    height: '100%',
    marginRight: theme.spacing(1),
  },
  suggestionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  noSuggestions: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  summaryBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
  },
}));

const AssignmentSuggestionsDialog = ({
  open,
  onClose,
  activeMarker,
  crews,
  weeklyRoutes,
  weeklyHours,
  sites,
  onSelectSuggestion,
  selectedDate
}) => {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && activeMarker) generateSuggestions();
  }, [open, activeMarker, crews, weeklyRoutes, weeklyHours]);

  const generateSuggestions = async () => {
    setLoading(true);
    try {
      const raw = suggestCrewAssignments(
        activeMarker,
        crews,
        weeklyRoutes,
        weeklyHours,
        sites,
        selectedDate,
        21 // Look ahead 3 weeks to find best options
      );
      setSuggestions(getTopSuggestions(raw, 10)); // Show top 10 suggestions
    } catch (error) {
      console.error('Error generating suggestions:', error);
      setSuggestions([]);
    }
    setLoading(false);
  };

  const getConfidenceColor = (level) => ({
    High: 'primary',
    Medium: 'default',
    Low: 'secondary'
  }[level] || 'default');

  const getConfidenceIcon = (level) => ({
    High: <CheckIcon />,
    Medium: <ScheduleIcon />,
    Low: <WarningIcon />
  }[level] || <WarningIcon />);

  const handleSelectSuggestion = (suggestion) => {
    onSelectSuggestion({
      crew: suggestion.crew,
      date: suggestion.date,
      site: activeMarker,
      suggestion,
    });
    onClose();
  };

  const formatHours = (hours) => `${hours ?? 0}h`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className={classes.suggestionHeader}>
          <Typography variant="h6">
            Assignment Suggestions for {activeMarker?.name}
          </Typography>
          <Chip
            icon={<TrendingUpIcon />}
            label={`${suggestions.length} Options`}
            color="primary"
            size="small"
          />
        </div>
        <Typography variant="body2" color="textSecondary">
          {activeMarker?.frequency} • {formatHours(activeMarker?.estimatedHours)}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box p={2}>
            <LinearProgress />
            <Typography align="center" variant="body2" style={{ marginTop: 16 }}>
              Analyzing crew schedules and finding optimal assignments...
            </Typography>
          </Box>
        ) : suggestions.length === 0 ? (
          <div className={classes.noSuggestions}>
            <WarningIcon color="disabled" style={{ fontSize: 48 }} />
            <Typography variant="h6" gutterBottom>
              No suitable assignments found
            </Typography>
            <Typography variant="body2">
              All crews may be too busy or the job requirements don't match available slots.
            </Typography>
          </div>
        ) : (
          <List>
            {suggestions.map((s, i) => (
              <ListItem
                key={i}
                button
                className={`${classes.suggestionItem} ${i === 0 ? classes.topSuggestion : ''}`}
                onClick={() => handleSelectSuggestion(s)}
              >
                <div className={classes.crewColorBar} style={{ backgroundColor: s.crew.color }} />

                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      {i === 0 && <StarIcon color="primary" fontSize="small" />}
                      <Typography variant="subtitle1">{s.crew.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        • {s.dateFormatted}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                      <Typography variant="caption">
                        Current: {formatHours(s.currentHours)} → {formatHours(s.estimatedTotalHours)}
                        {!s.hasAnyRoutes && ' (Empty Crew)'}
                        {s.hasAnyRoutes && !s.hasRoutesToday && ' (Free Today)'}
                      </Typography>
                      {s.reasoning && (
                        <Typography variant="caption" style={{ fontStyle: 'italic' }}>
                          {s.reasoning}
                        </Typography>
                      )}
                      <div className={classes.progressContainer}>
                        <Typography variant="caption">Score:</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(s.score, 100)}
                          style={{ width: 60, height: 4 }}
                        />
                        <Typography variant="caption">{s.score}</Typography>
                      </div>
                    </Box>
                  }
                />

                <ListItemSecondaryAction>
                  <div className={classes.scoreContainer}>
                    <Chip
                      icon={getConfidenceIcon(s.confidence)}
                      label={s.confidence}
                      color={getConfidenceColor(s.confidence)}
                      size="small"
                      className={classes.confidenceChip}
                    />
                    <Tooltip title="Select this assignment">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleSelectSuggestion(s)}
                      >
                        Select
                      </Button>
                    </Tooltip>
                  </div>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
        
        {/* Summary Section */}
        {suggestions.length > 0 && (
          <Box className={classes.summaryBox}>
            <Typography variant="caption" color="textSecondary">
              Found {suggestions.length} possible assignments across {Math.ceil(suggestions.length / crews.length)} days.
              {activeMarker?.preferredDay && (
                <> Job prefers {activeMarker.preferredDay}s
                  {activeMarker.frequency === 'Bi-Weekly' && ` (${activeMarker.evenOrOddWeeks} weeks)`}.
                </>
              )}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={generateSuggestions} disabled={loading}>
          Refresh Suggestions
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentSuggestionsDialog;
